import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Alert, MenuItem } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { setSessionTokens } from '../api/session';
import { sanitizeRichText } from '../utils/sanitizeHtml';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState('');
  const [disclaimer, setDisclaimer] = useState('');

  // TOTP state
  const [totpStep, setTotpStep] = useState<'login' | 'verify' | 'setup' | 'position'>('login');
  const [preAuthToken, setPreAuthToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpQr, setTotpQr] = useState('');
  const [totpSecret, setTotpSecret] = useState('');

  // Operator position state
  const [operatorPositions, setOperatorPositions] = useState<{id: number; name: string}[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [pendingLoginData, setPendingLoginData] = useState<any>(null);
  const [pbxEnabled, setPbxEnabled] = useState(false);

  useEffect(() => {
    fetch('/api/system-config/logo', { method: 'HEAD' })
      .then((r) => { if (r.ok) setLogoUrl('/api/system-config/logo'); })
      .catch(() => {});
    fetch('/api/system-config/public')
      .then((r) => r.json())
      .then((d) => {
        setTenantName(d.agency_name || d.jurisdiction_name || '');
        setDisclaimer(d.login_disclaimer || '');
        if (d.pbx_enabled) {
          setPbxEnabled(true);
          fetch('/api/operator-positions/active')
            .then((r2) => r2.json())
            .then((positions) => setOperatorPositions(positions || []))
            .catch(() => {});
        }
      })
      .catch(() => {});
    // Handle auto-login token from tenant admin portal
    const autoToken = searchParams.get('auto_token');
    if (autoToken) {
      localStorage.setItem('token', autoToken);
      api.get('/auth/me').then((res) => {
        const data = res.data;
        const userData = {
          user_id: data.id,
          username: data.username,
          full_name: data.full_name,
          role: data.role,
          must_change_password: data.must_change_password || false,
          dispatch_access: data.dispatch_access,
          mobile_access: data.mobile_access,
          admin_access: data.admin_access,
          superadmin_access: data.superadmin_access,
          require_2fa: data.require_2fa || false,
        };
        localStorage.setItem('user', JSON.stringify(userData));
        window.location.href = (userData.dispatch_access || userData.admin_access || userData.superadmin_access)
          ? '/dispatch/home'
          : userData.mobile_access ? '/mobile/mdt' : '/';
      }).catch(() => {
        localStorage.removeItem('token');
        setError('Auto-login token expired or invalid');
      });
    }
  }, [searchParams]);

  const finalizeLogin = (data: any) => {
    setSessionTokens('web', data.access_token, data.refresh_token);
    const userData = {
      user_id: data.user_id,
      username,
      full_name: data.full_name,
      role: data.role,
      must_change_password: data.must_change_password,
      dispatch_access: data.dispatch_access,
      mobile_access: data.mobile_access,
      admin_access: data.admin_access,
      superadmin_access: data.superadmin_access,
      require_2fa: data.require_2fa,
    };
    localStorage.setItem('user', JSON.stringify(userData));
    const dest = (userData.dispatch_access || userData.admin_access || userData.superadmin_access)
      ? '/dispatch/home'
      : userData.mobile_access ? '/mobile/mdt' : '/';
    window.location.href = dest;
  };

  const handleLoginSuccess = (data: any) => {
    if (pbxEnabled && operatorPositions.length > 0 && data.dispatch_access) {
      setPendingLoginData(data);
      setTotpStep('position');
      return;
    }
    finalizeLogin(data);
  };

  const handlePositionSelect = () => {
    if (selectedPosition && selectedPosition !== 'none') {
      localStorage.setItem('operator_position_id', selectedPosition);
      const match = operatorPositions.find((p) => String(p.id) === selectedPosition);
      if (match) localStorage.setItem('operator_position_name', match.name);
      else localStorage.removeItem('operator_position_name');
    } else {
      localStorage.removeItem('operator_position_id');
      localStorage.removeItem('operator_position_name');
    }
    finalizeLogin(pendingLoginData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post('/auth/login', { username, password });
      const data = res.data;

      if (data.totp_required) {
        setPreAuthToken(data.pre_auth_token);
        setTotpStep('verify');
        return;
      }
      if (data.totp_setup_required) {
        setPreAuthToken(data.pre_auth_token);
        setTotpQr(data.totp_qr || '');
        setTotpSecret(data.totp_secret || '');
        setTotpStep('setup');
        return;
      }

      handleLoginSuccess(data);
    } catch (err: any) {
      setError(err.response?.status === 403 && err.response?.data?.detail
        ? err.response.data.detail
        : 'Invalid username or password');
    }
  };

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post('/auth/verify-totp', {
        pre_auth_token: preAuthToken,
        totp_code: totpCode,
      });
      handleLoginSuccess(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid TOTP code');
    }
  };

  const handleTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post('/auth/setup-totp', {
        pre_auth_token: preAuthToken,
        totp_code: totpCode,
      });
      handleLoginSuccess(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid TOTP code');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#1a1a2e', py: 4, gap: 3 }}>
      <Paper sx={{ p: 4, width: 400 }}>
        {logoUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box component="img" src={logoUrl} alt="Agency Logo" sx={{ maxHeight: 100, maxWidth: 200, objectFit: 'contain' }} />
          </Box>
        )}
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          WatchCenter CAD
        </Typography>
        {tenantName && (
          <Typography variant="h6" align="center" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
            {tenantName}
          </Typography>
        )}
        <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
          Computer Aided Dispatch
        </Typography>
        {searchParams.get('idle') && !error && (
          <Alert severity="info" sx={{ mb: 2 }}>Signed out due to inactivity. Please sign in again.</Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {totpStep === 'login' && (
          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Username" margin="normal" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
            <TextField fullWidth label="Password" type="password" margin="normal" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large">
              Sign In
            </Button>
          </form>
        )}

        {totpStep === 'verify' && (
          <form onSubmit={handleTotpVerify}>
            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
              Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              fullWidth label="Authentication Code" margin="normal"
              value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus slotProps={{ htmlInput: { maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } } }}
            />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large" disabled={totpCode.length !== 6}>
              Verify
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => { setTotpStep('login'); setTotpCode(''); setError(''); }}>
              Back to Login
            </Button>
          </form>
        )}

        {totpStep === 'setup' && (
          <form onSubmit={handleTotpSetup}>
            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
              Set Up Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
            </Typography>
            {totpQr && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box component="img" src={totpQr} alt="TOTP QR Code" sx={{ width: 200, height: 200 }} />
              </Box>
            )}
            {totpSecret && (
              <Typography variant="caption" align="center" sx={{ display: 'block', mb: 2, wordBreak: 'break-all', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, fontFamily: 'monospace' }}>
                Manual entry key: {totpSecret}
              </Typography>
            )}
            <TextField
              fullWidth label="Enter code from app to verify" margin="normal"
              value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus slotProps={{ htmlInput: { maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } } }}
            />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} size="large" disabled={totpCode.length !== 6}>
              Verify & Complete Setup
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => { setTotpStep('login'); setTotpCode(''); setError(''); }}>
              Back to Login
            </Button>
          </form>
        )}

        {totpStep === 'position' && (
          <Box>
            <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
              Select Operator Position
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Choose the dispatch position you are sitting at, or select None.
            </Typography>
            <TextField
              select fullWidth label="Operator Position" margin="normal"
              value={selectedPosition} onChange={(e) => setSelectedPosition(e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              {operatorPositions.map((pos) => (
                <MenuItem key={pos.id} value={String(pos.id)}>{pos.name}</MenuItem>
              ))}
            </TextField>
            <Button fullWidth variant="contained" sx={{ mt: 2 }} size="large" onClick={handlePositionSelect}>
              Continue
            </Button>
          </Box>
        )}

      </Paper>
      {disclaimer && (
        <Paper
          sx={{
            p: 3, width: '100%', maxWidth: 720,
            fontSize: 12, lineHeight: 1.5, color: 'text.secondary',
            '& ul, & ol': { pl: 2.5, my: 0.5 }, '& h3': { m: 0, mb: 0.5, fontSize: 14 }, '& p': { m: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(disclaimer) }}
        />
      )}
    </Box>
  );
}
